/**
 * Quick 260916-fk0 — optional board column ordering.
 * MKS-234 — adds a third mode, `priority`.
 *
 * The board's default order is left exactly as it was; `recentlyUpdated` puts
 * the freshest task at the top of every column, and `priority` orders every
 * column Urgent, High, Normal, Low.
 *
 * Client-side on purpose. The backend does support `?sort=lastActivityAt:desc`
 * / `?sort=priority:*` (BOARD_SORT_FIELDS / sortCards), but `useBoard` fetches
 * the board with no query params and every other refinement — the MKS-134
 * filter bar, the MKS-142 text search — already runs in memory over the
 * loaded board. Routing the sort through the server would split one
 * behaviour across two mechanisms, add a round-trip and a query-key
 * dimension, and make sorting re-fetch while filtering stays instant.
 *
 * "Freshest" is `lastActivityAt`, which the card DTO already carries. It comes
 * from computeTaskActivity (comments, time entries, edits) rather than a bare
 * row `updatedAt`, so it tracks real movement on a task.
 *
 * The `priority` comparator mirrors `sortCards`/`PRIORITY_RANK` in
 * backend/src/services/projectService.ts exactly (same rank-by-index, same
 * createdAt-then-id tie-break), so a client sort and a server sort of the
 * same cards can never disagree.
 */
import type { BoardResponse, TaskPriority } from '../services/api/board';

type BoardColumn = BoardResponse['columns'][number];
type BoardTask = BoardColumn['tasks'][number];

export const BOARD_SORT_MODES = ['default', 'recentlyUpdated', 'priority', 'dueDate'] as const;
export type BoardSortMode = (typeof BOARD_SORT_MODES)[number];

export const BOARD_SORT_STORAGE_KEY = 'board.sortMode';

export const BOARD_SORT_LABELS: Record<BoardSortMode, string> = {
  default: 'Default',
  recentlyUpdated: 'Recently updated',
  priority: 'Priority',
  dueDate: 'Due date', // MKS-341
};

/**
 * A persisted value is browser-writable, so anything outside the known modes
 * must never reach the comparator — same T-86-13 reasoning as
 * `readInitialSortMode` in MyTasksPage.tsx.
 */
export function readInitialBoardSortMode(): BoardSortMode {
  try {
    const stored = sessionStorage.getItem(BOARD_SORT_STORAGE_KEY);
    if (stored && (BOARD_SORT_MODES as readonly string[]).includes(stored)) {
      return stored as BoardSortMode;
    }
  } catch {
    // sessionStorage unavailable (private browsing edge) — fall through.
  }
  return 'default';
}

export function persistBoardSortMode(mode: BoardSortMode): void {
  try {
    sessionStorage.setItem(BOARD_SORT_STORAGE_KEY, mode);
  } catch {
    // Non-fatal: the mode still applies for this session.
  }
}

function millis(value: string | null | undefined): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

// MKS-234 — severity-ordered tuple, urgent first. Mirrors TASK_PRIORITIES in
// backend/src/api/schemas/taskMutation.ts and PRIORITY_RANK in
// backend/src/services/projectService.ts: rank is the array index, not
// alphabetical order (alphabetically `high` would beat `urgent`). Declared
// locally rather than shared, following the existing precedent of
// utils/boardFilterUrl.ts, components/kanban/PriorityFilterToggle.tsx and
// components/kanban/TaskPriorityPicker.tsx, each of which already declares its
// own copy of this tuple.
const TASK_PRIORITIES: TaskPriority[] = ['urgent', 'high', 'normal', 'low'];

/**
 * Rank of a priority value, lowest (0) is most severe. An absent or
 * unrecognised value ranks `TASK_PRIORITIES.length` — i.e. LAST — exactly
 * like PRIORITY_RANK[c.priority] ?? TASK_PRIORITIES.length on the backend.
 * `TaskCard.priority` is optional on the wire type only for legacy fixtures
 * (frontend/src/services/api/board.ts), so the undefined branch here is a
 * type-level reality rather than defensive padding.
 */
function priorityRank(value: string | null | undefined): number {
  if (!value) return TASK_PRIORITIES.length;
  const i = TASK_PRIORITIES.indexOf(value as TaskPriority);
  return i === -1 ? TASK_PRIORITIES.length : i;
}

/**
 * Tie-break shared by every comparator below: createdAt ascending then id
 * ascending. Load-bearing rather than tidiness — the board refetches on a 5s
 * poll, and without a total order two cards sharing a key would trade places
 * on every poll. Both `compareRecentActivity` and `comparePriority` call this
 * one helper so they can never drift apart.
 */
function compareByCreatedThenId(a: BoardTask, b: BoardTask): number {
  const byCreated = millis(a.createdAt) - millis(b.createdAt);
  if (byCreated !== 0) return byCreated;
  return a.id - b.id;
}

/** Newest activity first; falls through to the shared createdAt/id tie-break. */
function compareRecentActivity(a: BoardTask, b: BoardTask): number {
  const byActivity = millis(b.lastActivityAt) - millis(a.lastActivityAt);
  if (byActivity !== 0) return byActivity;
  return compareByCreatedThenId(a, b);
}

/**
 * Urgent, High, Normal, Low; falls through to the shared createdAt/id
 * tie-break. Mirrors `sortCards`'s `priority` branch in
 * backend/src/services/projectService.ts exactly.
 */
function comparePriority(a: BoardTask, b: BoardTask): number {
  const byRank = priorityRank(a.priority) - priorityRank(b.priority);
  if (byRank !== 0) return byRank;
  return compareByCreatedThenId(a, b);
}

/**
 * MKS-341 — earliest due date first; a ticket with NO due date sorts LAST
 * (never collapsed to the front), then the shared createdAt/id tie-break.
 *
 * Compared as day strings, not parsed Dates: `TaskCard.dueDate` is already a
 * zero-padded `YYYY-MM-DD`, which compares lexicographically in chronological
 * order, and parsing would reintroduce the timezone question the calendar-day
 * shape exists to avoid.
 *
 * Mirrors `myTasksCompost.dueDateCompare`'s "no due date sorts last" rule, so
 * the board's due sort and My Tasks' due sort agree about the undated tail.
 */
function compareDueDate(a: BoardTask, b: BoardTask): number {
  const ad = a.dueDate ?? null;
  const bd = b.dueDate ?? null;
  if (ad !== null && bd !== null && ad !== bd) return ad < bd ? -1 : 1;
  if (ad !== null && bd === null) return -1;
  if (ad === null && bd !== null) return 1;
  return compareByCreatedThenId(a, b);
}

/**
 * Sort every column's tasks per `mode`.
 *
 * Returns the input array BY IDENTITY for the default mode, matching
 * filterBoardColumns' D-05 short-circuit so downstream props stay referentially
 * stable and nothing re-renders while the option is off. Never mutates the
 * input — those arrays belong to the query cache.
 */
export function sortBoardColumns<T extends BoardColumn>(columns: T[], mode: BoardSortMode): T[] {
  if (mode === 'default') return columns;

  const cmp =
    mode === 'priority'
      ? comparePriority
      : mode === 'dueDate'
        ? compareDueDate // MKS-341
        : compareRecentActivity;

  return columns.map((col) => ({
    ...col,
    tasks: [...col.tasks].sort(cmp),
  }));
}
